/**
 * Utility for constructing sized arche icon URLs.
 *
 * Icons are stored in S3 at multiple sizes:
 *   {archId}/icon.png      — 1024px original
 *   {archId}/icon-512.png  — 512px
 *   {archId}/icon-256.png  — 256px
 *   {archId}/icon-128.png  — 128px
 *   {archId}/icon-64.png   — 64px
 */

import type { ImgHTMLAttributes } from 'react';

export type IconSize = 64 | 128 | 256 | 512;

/**
 * Derive a sized icon URL from the original image_ref.
 * Sized variants are always PNG (generated by sharp), so the extension is
 * replaced with .png regardless of the original format (jpg, webp, etc.).
 * Returns the original URL if no size is specified or the URL doesn't match the expected pattern.
 */
export function archeIconUrl(imageRef: string | null | undefined, size?: IconSize): string | null {
  if (!imageRef) return null;
  if (!size) return imageRef;
  const replaced = imageRef.replace(/\/icon\.\w+$/, `/icon-${size}.png`);
  // If the replacement didn't change anything, the URL doesn't match our pattern — return original
  return replaced === imageRef ? imageRef : replaced;
}

/**
 * onError handler for arche icon <img> elements.
 * Falls back to the original unsized URL if the sized variant fails to load.
 */
export function archeIconOnError(
  imageRef: string,
  e: React.SyntheticEvent<HTMLImageElement>,
): void {
  if (e.currentTarget.src !== imageRef) {
    e.currentTarget.src = imageRef;
  }
}

/**
 * Props for ArcheIconImg — a sized arche icon with automatic fallback.
 */
export interface ArcheIconImgProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  imageRef: string;
  size: IconSize;
}

/**
 * Renders a sized arche icon <img> that falls back to the original URL on error.
 */
export function ArcheIconImg({ imageRef, size, ...imgProps }: ArcheIconImgProps) {
  return (
    <img
      src={archeIconUrl(imageRef, size) ?? ''}
      onError={(e) => archeIconOnError(imageRef, e)}
      {...imgProps}
    />
  );
}
